/**
 * SA-1030 (spec D17): "Is this an update to a <noun> we already have?" on a
 * waiting WhatsApp group row that triage matched. Each candidate shows the
 * server's summary and what the message would change; the person picks one,
 * types another number, or says it is new. With a likely or cited candidate
 * nothing is preselected, so a fast click can neither birth a duplicate nor
 * land on the wrong record. The block sends nothing itself: the panel's
 * `run` makes the decision. The block also shows on an email message on a
 * thread with no record of its own (SA-1062), the candidates being the
 * records its sender and words point to.
 */
import { Fragment, useId, useState } from 'react'
import { Badge, Button } from '@/components/ui'
import type { TriageKindOption, TriageMatch } from '@/types/inbox'
import { matchText, WORKING_LABEL } from './triageText'

type Choice = { type: 'record'; number: string } | { type: 'other' } | { type: 'new' } | null

interface TriageMatchBlockProps {
  match: TriageMatch
  /** The row's proposed kind: "new" is preselected only when that is the record kind. */
  proposedKind: string | null
  kinds: TriageKindOption[]
  /** The record noun, lower case. */
  noun: string
  busy: boolean
  /** Which of the block's two actions is held, if either. */
  working: 'update' | 'create' | 'close' | null
  /** Send the message to this record: an update, or a close on a `close` block. */
  onUpdate: (recordNumber: string) => void
  onCreate: () => void
}

export function TriageMatchBlock({ match, proposedKind, kinds, noun, busy, working, onUpdate, onCreate }: TriageMatchBlockProps) {
  const name = useId()
  const closing = match.action === 'close'
  const strong = match.candidates.some((candidate) => candidate.likely || candidate.cited)
  // A message judged an update never starts on "new" (F6): a fast click would birth a record from it.
  const startNew = !strong && !!match.newKind && proposedKind === match.newKind
  const [choice, setChoice] = useState<Choice>(startNew ? { type: 'new' } : null)
  const [other, setOther] = useState('')
  const updateOption = kinds.find((kind) => kind.id === match.updateKind)
  const newOption = kinds.find((kind) => kind.id === match.newKind)
  const canUpdate = !!match.updateKind && !!updateOption?.canDecide
  const canCreate = !!match.newKind && !!newOption?.canDecide
  const typed = other.trim()
  const chosen = choice?.type === 'record' ? choice.number : choice?.type === 'other' ? typed : ''
  const ready = choice !== null && (choice.type === 'new' ? canCreate : canUpdate && !!chosen)
  const label = working ? WORKING_LABEL[working]
    : choice === null ? matchText.choose
    : choice.type === 'new' ? matchText.create(match.newLabel ?? noun)
    : closing ? matchText.close(chosen || noun)
    : matchText.update(chosen || noun)
  const refusals = [newOption, updateOption].filter(
    (option, index, all): option is TriageKindOption =>
      !!option && !option.canDecide && !!option.refusal && all.findIndex((o) => o?.id === option.id) === index)

  const submit = () => {
    if (!ready || choice === null) return
    if (choice.type === 'new') onCreate()
    else onUpdate(chosen)
  }

  return (
    <fieldset className="space-y-1.5 rounded-md border border-border bg-background/60 p-2">
      <legend className="px-1 text-xs font-medium text-foreground">{closing ? matchText.closeHeading(noun) : matchText.heading(noun)}</legend>
      {match.note && <p className="text-xs text-muted-foreground">{match.note}</p>}
      {!match.updateKind && <p className="text-xs text-muted-foreground">{matchText.noUpdateKind(noun)}</p>}
      {match.candidates.map((candidate) => (
        <div key={candidate.recordId} className="space-y-0.5">
          <label className="flex items-start gap-2 text-xs text-foreground">
            <input type="radio" name={name} disabled={busy || !canUpdate}
              checked={choice?.type === 'record' && choice.number === candidate.recordNumber}
              onChange={() => setChoice({ type: 'record', number: candidate.recordNumber })} />
            <span>
              <span className="font-medium">{candidate.recordNumber}</span> · {candidate.summary}
              {candidate.likely && <Badge variant="warning-soft" className="ml-1.5">{matchText.likely}</Badge>}
            </span>
          </label>
          {candidate.changes.length > 0 && (
            <dl className="ml-6 grid grid-cols-[minmax(6.5rem,max-content)_minmax(0,1fr)] gap-x-3 text-xs">
              {candidate.changes.map((change) => (
                <Fragment key={change.label}>
                  <dt className="text-muted-foreground">{change.label}</dt>
                  <dd className="min-w-0 [overflow-wrap:anywhere]">{`${change.current ?? '—'} → ${change.proposed}`}</dd>
                </Fragment>
              ))}
            </dl>
          )}
          {candidate.moreChanges > 0 && (
            <p className="ml-6 text-xs text-muted-foreground">{matchText.more(candidate.moreChanges)}</p>
          )}
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2 text-xs text-foreground">
        <label className="flex items-center gap-2">
          <input type="radio" name={name} disabled={busy || !canUpdate} checked={choice?.type === 'other'}
            onChange={() => setChoice({ type: 'other' })} />
          {matchText.another(noun)}
        </label>
        <input type="text" aria-label={matchText.anotherInput(noun)} value={other} disabled={busy || !canUpdate}
          onChange={(event) => { setOther(event.target.value); setChoice({ type: 'other' }) }}
          className="w-44 rounded-md border border-border bg-background px-2 py-1 text-xs" />
      </div>
      {match.newKind && (
        <label className="flex items-center gap-2 text-xs text-foreground">
          <input type="radio" name={name} disabled={busy} checked={choice?.type === 'new'}
            onChange={() => setChoice({ type: 'new' })} />
          {matchText.createChoice(match.newLabel ?? noun)}
        </label>
      )}
      <Button size="sm" disabled={busy || !ready} isLoading={working !== null} className="text-xs" onClick={submit}>
        {label}
      </Button>
      {refusals.map((option) => (
        <p key={option.id} className="text-xs text-muted-foreground">{option.refusal}</p>
      ))}
    </fieldset>
  )
}
